import { Box } from "@mui/material";
import { sanitize } from "../../../helpers/html";
import { useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./richTextMessageEditor.css";

const Quill = ReactQuill as any;

// Override Quill's internal Link.sanitize, which Quill calls automatically on every link
// creation/edit, to prepend https:// when the user omits the protocol.
const Link = Quill.Quill?.import("formats/link");
if (Link) {
  const originalSanitize = Link.sanitize.bind(Link);
  Link.sanitize = (url: string) => {
    if (url && !/^https?:\/\//i.test(url)) {
      return originalSanitize(`https://${url}`);
    }
    return originalSanitize(url);
  };
}

type Props = {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  showToolbar?: boolean;
  disabled?: boolean;
  className?: string;
  height?: number | string;
};

const RichTextMessageEditor = ({
  value,
  onChange,
  readOnly = false,
  showToolbar = true,
  disabled = false,
  className,
  height = 280,
}: Props) => {
  const lastInternalValueRef = useRef<string>("");

  const handleChange = useCallback(
    (newHtml: string) => {
      lastInternalValueRef.current = newHtml;
      onChange?.(newHtml);
    },
    [onChange],
  );

  // When value matches what Quill last emitted, pass it back unchanged to avoid
  // DOMPurify re-normalization causing a phantom setContents() call that resets cursor.
  const quillValue =
    value === lastInternalValueRef.current ? value : sanitize(value);

  const quillModules = useMemo(() => {
    return {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "link"],
        [{ list: "ordered" }, { list: "bullet" }],
      ],
      clipboard: {
        matchVisual: false,
      },
    };
  }, []);

  const quillFormats = useMemo(
    () => ["header", "bold", "italic", "underline", "link", "list", "bullet"],
    [],
  );

  const isReadOnly = readOnly || disabled;
  const shouldShowToolbar = showToolbar && !isReadOnly;

  return (
    <Box className={`message-editor-root ${className ?? ""}`}>
      <Box className="message-editor-body" style={{ height }}>
        <Quill
          key={`rte-${shouldShowToolbar ? "toolbar" : "no-toolbar"}-${
            isReadOnly ? "readonly" : "editable"
          }`}
          theme="snow"
          value={quillValue}
          onChange={isReadOnly ? undefined : handleChange}
          modules={shouldShowToolbar ? quillModules : { toolbar: false }}
          formats={quillFormats}
          readOnly={isReadOnly}
          className={`message-editor-quill ${
            shouldShowToolbar ? "" : "no-toolbar"
          }`}
        />
      </Box>
    </Box>
  );
};

export default RichTextMessageEditor;
